<template>
  <div class="address-autocomplete">
    <base-input
      class="col-xs-12 mb10 input shipping-block-field address-autocomplete__input-field"
      :class="{'clearable': activeClearIcon}"
      type="text"
      id="address-autocomplete"
      autocomplete="street-address"
      name="address-autocomplete"
      :placeholder="$t('Enter your address')"
      v-model.trim="searchField.value"
      @input="searchAddress"
      @focus="handleSearchFieldFocus(true)"
      @blur="handleSearchFieldFocus(false)"
      :validations="[{
        condition: $v.address.$error && !$v.address.required && !searchField.value.length,
        text: $t('Field is required.')
      }]"
    />
    <i v-if="activeClearIcon" class="material-icons p15 cl-bg-tertiary address-autocomplete__clear-icon" @click="clearSearchResults">close</i>
    <ul class="addresses-list" :class="{active: showAddressesDropdown}">
      <template v-if="addresses.length">
        <li v-for="(address, index) in addresses" :key="index" @click="selectAddress(address)">
          {{ address.display_name }}
        </li>
      </template>
      <template v-else>
        <li class="no_action">
          <dropdown-search-result-loader v-if="searchField.value.length > 3 && searchField.loading" />
          <template v-else>
            {{ emptyResultMessage }}
          </template>
        </li>
      </template>
      <li v-if="showMapModal" class="no_action address_btn__wrapper">
        <mini-button-full class="address_btn" title="Select the address on the map" @click.native="showMapModalOverlay ? $emit('handleOpenOverlayMapModal') : openModal()">
          {{ $t('Select the address on the map') }}
        </mini-button-full>
      </li>
    </ul>
    <address-map-modal
      v-if="showMapModal && !showMapModalOverlay"
      :open-modal="showModal"
      :search-address="searchAddress"
      :addresses="addresses"
      @address-selected="selectAddress"
      @close="closeModal"
    />
  </div>
</template>

<script>
import config from 'config'
import deepParse from '@vue-storefront/core/scripts/utils/deep-parse';
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import DropdownSearchResultLoader from 'theme/components/core/DropdownSearchResultLoader.vue'

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const MiniButtonFull = () => import(/* webpackChunkName: "vsf-mini-button-full" */ 'theme/components/theme/MiniButtonFull.vue');
const AddressMapModal = () => import(/* webpackChunkName: "vsf-address-map-modal" */ 'theme/components/core/blocks/MyAccount/ShippingDetails/AddressMapModal.vue');

export default {
  name: 'BaseAddressAutocompleteInput',
  components: {
    DropdownSearchResultLoader,
    AddressMapModal,
    MiniButtonFull,
    BaseInput
  },
  props: {
    showMapModal: {
      type: Boolean,
      required: false,
      default: true
    },
    showMapModalOverlay: {
      type: Boolean,
      required: false,
      default: false
    },
    clearInput: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    searchField: {
      value: '',
      loading: true,
      hasFocus: false,
      focusTimeOut: null
    },
    addresses: [],
    address: null,
    isFullAddressStr: /\d.*\s|\s.*\d/,
    timeout: null,
    showModal: false
  }),
  validations () {
    return {
      address: {
        required
      }
    }
  },
  computed: {
    showAddressesDropdown () {
      return this.searchField.hasFocus;
    },
    emptyResultMessage () {
      return this.isFullAddressStr.test(this.searchField.value) ? this.$t('Not results found') : this.$t('Enter full address')
    },
    activeClearIcon () {
      return !!(this.searchField.value.length || this.addresses.length)
    }
  },
  beforeMount () {
    this.$bus.$on('address-autocomplete-set-search', this.loadSearchValue);
    this.$bus.$on('address-autocomplete-validate', (cb) => {
      const valid = this.validate();
      cb && cb(valid);
    });
  },
  methods: {
    ...mapActions({
      setSelectedAddress: 'address/setSelectedAddress'
    }),
    handleSearchFieldFocus (state) {
      this.searchField.focusTimeOut = setTimeout(() => {
        this.searchField.hasFocus = state;
      }, 200);

      if (!state) {
        this.$v.address.$touch();
      }
    },
    loadSearchValue (shipping) {
      if (typeof shipping === 'string') {
        this.searchField.value = shipping;
        this.searchAddress();
        return
      }
      if (shipping.api_details) {
        let details = shipping.api_details
        if (typeof details === 'string') {
          try {
            details = deepParse(details)
          } catch (e) {
            console.error('Error during attempt to parse address details', e)
          }
        }
        this.searchField.value = details.display_name
        this.searchAddress()
        return
      }
      if (shipping.street && shipping.house) {
        this.searchField.value = `${shipping.street} ${shipping.house}`
        this.searchField.hasFocus = true
      }
    },
    searchAddress () {
      this.searchField.loading = true;
      clearTimeout(this.timeout)
      if (!this.searchField.value || this.searchField.value.length < 3) {
        this.addresses = [];
        this.searchField.loading = true;
        return
      }
      this.timeout = setTimeout(() => {
        fetch(`https://${config.rad.base_url}/address/suggestion?address=${encodeURIComponent(this.searchField.value)}&language=${encodeURIComponent(this.$store.state.languages.currentLanguage)}`)
          .then(response => response.json())
          .then(data => {
            this.addresses = data;
          })
          .catch(error => {
            console.error('Error fetching data from Photon API: ', error);
            this.addresses = [];
          })
          .finally(() => {
            this.searchField.loading = false;
          })
      }, 500)
    },
    selectAddress (address) {
      if (!address) {
        this.address = null;
        return;
      }
      if (!address.hasOwnProperty('display_name')) {
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('The selected address is not valid. Please, edit it or contact the support!'),
          action1: { label: this.$t('OK') }
        })
        return;
      }
      this.address = address;
      this.searchField.value = address.display_name;
      this.timeout = setTimeout(() => {
        this.searchField.hasFocus = false;
      }, 500)
      this.$emit('address-selected', address);
      this.setSelectedAddress(address);
      this.$bus.$emit('address-autocomplete-set-search', address.display_name);
    },
    clearSearchResults () {
      this.searchField.value = '';
      this.searchField.loading = true;
      this.timeout = setTimeout(() => {
        this.addresses = [];
      }, 500)
      this.address = null;
      this.$emit('address-selected', null);
      this.loadSearchValue('');
      this.$v.address.$touch();
    },
    openModal () {
      this.$bus.$emit('modal-toggle', 'modal-address-map')
      this.showModal = true
    },
    closeModal () {
      this.showModal = false;
    },
    validate () {
      this.$v.address.$touch();
      return !this.$v.address.$invalid;
    }
  },
  beforeDestroy () {
    this.$bus.$off('address-autocomplete-set-search', this.loadSearchValue);
    this.$bus.$off('address-autocomplete-validate', (cb) => {
      const valid = this.validate();
      cb && cb(valid);
    });
    if (this.timeout) clearTimeout(this.timeout);
    if (this.searchField.focusTimeOut) clearTimeout(this.searchField.focusTimeOut);
  },
  watch: {
    showModal (value) {
      if (!value && !this.address) {
        this.clearSearchResults();
      }
    },
    clearInput (value) {
      if (value) {
        this.clearSearchResults();
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .address-autocomplete {
    position: relative;
    &__clear-icon {
      position: absolute;
      z-index: 5;
      top: 0;
      right: 0;
      cursor: pointer;
      padding: 0;
      margin: 10px 16px;
    }
    .addresses-list {
      text-align: left;
      display: flex;
      flex-direction: column;
      border: 1px solid #bdbdbd;
      border-radius: 5px;
      margin: 0 8px;
      padding: 0;
      list-style: none;
      position: absolute;
      background: white;
      z-index: 3;
      top: 40px;
      left: 0;
      border-top: none;
      box-shadow: 0px 2px 5px 0px #00000033;
      row-gap: 4px;
      width: 100%;
      max-width: -webkit-fill-available;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-10%);
      transition: all 0.3s ease;
      max-height: calc(160px + 48px); //48px - button height
      overflow: auto;
      scrollbar-width: thin;
      .address_btn__wrapper {
        position: sticky;
        bottom: 0;
        background: white;
        padding-top: 10px;
      }
      &.active {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
      >li {
        padding: 4px 18px;
        border-radius: 5px;
        &:first-of-type {
          padding-top: 14px;
        }
        &:last-of-type {
          padding-bottom: 14px;
        }
        &:not(.no_action) {
          cursor: pointer;
          &:hover {
            background-color: #F2F2F2;
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  .address-autocomplete {
    .address_btn {
      width: 100%;
      background: #11bb2e;
      border: none;
      border-radius: 5px;
      padding: 12px;
      color: white;
      &:hover {
        background: rgba(17, 187, 46, 0.8);
      }
    }
    &__input-field.clearable input {
      padding-right: 32px;
    }
    &__input-field {
      .base-input {
        position: relative;
        z-index: 4;
      }
    }
  }
</style>
